import type { Currency, SubscriptionModel, TaxId, TaxInfo } from '@hero/schema';
import {
  DEFAULT_SUBSCRIPTION_BUSINESS,
  SUBSCRIPTION_CONFIG,
  getPlanSubscriptionModel,
  usagePricePerUnit,
  type SubscriptionAudience,
  type SubscriptionInterval,
  type UsageTierConfig,
} from '../config/subscription.config';
import {
  getPlanBusiness,
  planUnit,
  type Business,
} from '../config/app-registry.config';
import { amountForUsagePurchase, amountFromCatalogPrice } from '../amount/amountForUsagePurchase';
import { roundPrice } from '../price/round.price';

/**
 * Itemized preview of what a purchase would cost, in a given currency.
 *
 * Single source of truth for the checkout summary card and any
 * server-side preview endpoint — the math here MUST match what the
 * `subscription.subscribe` / `paySubscription` backend paths use when
 * they create the initial Billing.
 *
 * Two shapes of purchase land on this one result, selected by the
 * plan's {@link SubscriptionModel}:
 *
 * **`period`** — renting seats for a stretch of time:
 *
 *   subtotalPerMonth = basePricePerMonth + perVehicleRate × committedQty
 *   subtotal         = subtotalPerMonth × monthsInInterval  (rounded)
 *
 * **`usage`** — buying a bucket of credits outright:
 *
 *   subtotal         = tierRate(qty) × qty                  (rounded)
 *
 * then both go through {@link amountForUsagePurchase} /
 * {@link amountFromCatalogPrice} (same math as `AmountService`):
 *
 *   exclusive — catalog × qty is pre-tax; VAT added on top
 *   inclusive — catalog × qty is the buyer-facing total; VAT split out
 *
 * The interval is absent from the usage formula, not merely equal to
 * one. Credits do not expire, so there is no stretch of time to
 * multiply by — the discount comes from *volume* instead, via the
 * ladder in `SUBSCRIPTION_CONFIG.usage.orderTiers`. An earlier version
 * of this file did run order pricing through `× monthsInInterval`, on
 * the theory that a committed monthly volume was the same shape as a
 * committed seat count. It is not, and that is what this split fixes.
 */
export interface SubscriptionPreview {
  planCode: string;
  /**
   * Which formula produced this — see {@link SubscriptionModel}. Read
   * it before rendering anything time-shaped: on `usage` the
   * `interval` and `monthsInInterval` below are placeholders, not
   * facts about the purchase.
   */
  model: SubscriptionModel;
  interval: SubscriptionInterval;
  currency: Currency;
  /** What this plan meters — decides which quantity `committedQty` is. */
  billingBasis: 'per_vehicle' | 'per_order';
  /**
   * The quantity being priced: committed vehicle seats on a `period`
   * plan, credits being bought on a `usage` one.
   */
  committedQty: number;
  /**
   * Number of committed seats the user is paying for.
   *
   * @deprecated Use `committedQty` with `billingBasis`. Kept because it
   * is zero on a `per_order` plan, which is the correct seat count but a
   * misleading thing to render — call sites that show it unconditionally
   * are the ones that need migrating.
   */
  vehicleQty: number;
  /**
   * Number of months covered by one billing period of this interval.
   * `month -> 1`, `semester -> 6`, `year -> 12`.
   *
   * Always `1` on `usage`, where it is a divisor-of-one kept so shared
   * arithmetic does not need a branch — not a claim that the credits
   * last a month.
   */
  monthsInInterval: number;
  /**
   * Total units the purchase buys — `committedQty × monthsInInterval`
   * on `period`, and simply `committedQty` on `usage`.
   *
   * On `period` this is seat-months, which is rarely worth rendering.
   * On `usage` it is the number of credits added to the bucket, which
   * is the headline figure.
   */
  committedQtyForPeriod: number;
  /**
   * Pre-tax total for the purchase — see the formulas on
   * {@link SubscriptionPreview}.
   *
   * The rates are read from `SUBSCRIPTION_CONFIG` inside this function
   * but not surfaced on the preview — the UI either shows this
   * subtotal directly (order summary) or the apples-to-apples
   * `perUnitPrice` (plan cards).
   */
  subtotal: number;
  /**
   * Tax info applied for this currency, or `null` when the currency
   * isn't taxed in v1 (e.g. USD). Amounts come from
   * {@link amountForUsagePurchase} / {@link amountFromCatalogPrice}.
   */
  tax: TaxAppliedInfo | null;
  /** Canonical total — tax-in stays the catalog total; tax-out is subtotal + VAT. */
  total: number;
  /** `true` when the resolved plan has no per-month price (e.g. `free`). */
  isFree: boolean;
  /**
   * Effective unit price at the quantity being bought, with any base
   * price amortized across the units.
   *
   * The unit depends on the model: on `period` this is **per truck per
   * month**; on `usage` it is **per credit**, which is exactly the
   * ladder rate the buyer was quoted. Read it together with `model` to
   * pick the right label.
   *
   * Apples-to-apples comparison number for the plan cards, so users can
   * tell which plan is cheaper regardless of which interval pill is
   * selected.
   *
   * Zero when `committedQty` is zero.
   */
  perUnitPrice: number;
  /**
   * @deprecated Use `perUnitPrice` with `billingBasis`. Only meaningful
   * on `per_vehicle` plans; on a `per_order` plan this is the per-order
   * rate and calling it "per truck" is a lie.
   */
  perTruckPerMonth: number;
}

export interface TaxAppliedInfo {
  id: TaxInfo['id'];
  /** Tax rate as a decimal (e.g. `0.15` = 15%). */
  percentage: number;
  /** Tax amount in the currency, rounded to the config's decimal places. */
  amount: number;
}

export interface ComputeSubscriptionPreviewInput {
  planCode: string;
  /** Ignored on `usage` plans, which have no interval to commit to. */
  interval: SubscriptionInterval;
  /** Committed vehicle seats. Read only on `period` plans. */
  committedVehicleQty?: number;
  /** Credits being bought. Read only on `usage` plans. */
  committedOrderQty?: number;
  currency: Currency;
  /**
   * Which ladder to price a `usage` purchase against. Defaults to the
   * plan's own business, which is right for every plan that belongs to
   * one — pass it only to disambiguate.
   */
  business?: Business;
  /** @deprecated Use {@link ComputeSubscriptionPreviewInput.business}. */
  line?: Business;
  /**
   * Which of the line's ladders to price against — a company and a
   * freelancer can be quoted differently for the same credits. Read
   * only on `usage` plans; defaults to `org`.
   */
  audience?: SubscriptionAudience;
  /**
   * Sticky experiment overlay. When set, quote and charge share this
   * ladder rather than re-reading the catalog / PostHog.
   */
  tiers?: ReadonlyArray<UsageTierConfig>;
  /** Whether catalog `pricePerUnit` already includes VAT. Defaults false. */
  taxInclusive?: boolean;
  /** `null` = no tax. Omit to use `taxByCurrency[currency]`. */
  taxId?: TaxId | null;
  /**
   * Seat price quoted from a sales config (catalog or PostHog) instead of
   * the plan's own `perVehiclePricePerMonth`. Read only on `period` plans.
   * `taxInclusive` here interprets this rate, independently of the
   * ladder's `taxInclusive` above.
   */
  seatPrice?: { perVehiclePricePerMonth: number; taxInclusive?: boolean };
}

/**
 * Compute a `SubscriptionPreview` from `SUBSCRIPTION_CONFIG`.
 *
 * Returns `null` only when the planCode is unknown (caller's bug);
 * for `free` / `custom` (no per-month price), still returns a
 * structured zero-cost preview so the UI can render a coherent
 * "no charge" or "contact sales" summary line.
 *
 * Passing the quantity for the *other* basis is not an error — it is
 * simply ignored. The checkout keeps both numbers in state while the
 * user flips between plans, so tolerating the unused one avoids making
 * every caller pre-resolve the basis before it can ask for a price.
 */
export function computeSubscriptionPreview(
  input: ComputeSubscriptionPreviewInput,
): SubscriptionPreview | null {
  const { planCode, interval, currency } = input;
  const plan = SUBSCRIPTION_CONFIG.plans[planCode];
  if (!plan) return null;

  const billingBasis =
    planUnit(planCode) === 'vehicle' ? ('per_vehicle' as const) : ('per_order' as const);
  const model = getPlanSubscriptionModel(planCode);
  const isUsage = model === 'usage';

  const rawQty = isUsage ? input.committedOrderQty : input.committedVehicleQty;
  const committedQty = Math.max(0, Math.floor(rawQty ?? 0));

  // The credits are bought outright, so the ladder rate for this
  // quantity *is* the price — nothing is amortized over time and the
  // interval never enters. `monthsInInterval` stays 1 below so the
  // shared arithmetic and the rendered breakdown both stay honest.
  const business =
    input.business ??
    input.line ??
    getPlanBusiness(planCode) ??
    DEFAULT_SUBSCRIPTION_BUSINESS;
  const basePricePerMonth = isUsage
    ? 0
    : (plan.basePricePerMonth?.[currency]?.[interval] ?? 0);
  const unitPrice = isUsage
    ? (usagePricePerUnit(business, committedQty, currency, input.audience, input.tiers) ?? 0)
    : (input.seatPrice?.perVehiclePricePerMonth ??
      plan.perVehiclePricePerMonth?.[currency]?.[interval] ??
      0);

  const subtotalPerMonth = basePricePerMonth + unitPrice * committedQty;

  const monthsInInterval = isUsage ? 1 : getMonthsInInterval(interval);
  // `× months` is the one and only inflator from per-month rates to a
  // per-period total. roundPrice absorbs any IEEE-754 cent drift from
  // the per-month decimals (e.g. 41.6666… × 12 → 500).
  const catalogAmount = roundPrice(subtotalPerMonth * monthsInInterval);

  const taxFromCurrency = SUBSCRIPTION_CONFIG.taxByCurrency[currency] ?? null;
  const taxId = input.taxId !== undefined ? input.taxId : (taxFromCurrency?.id ?? null);
  const taxInclusive = input.taxInclusive ?? false;

  const amount = isUsage
    ? amountForUsagePurchase({
        qty: committedQty,
        pricePerUnit: unitPrice,
        currency,
        taxId,
        taxInclusive,
      })
    : amountFromCatalogPrice({
        currency,
        catalogAmount,
        taxId,
        taxInclusive: input.seatPrice?.taxInclusive ?? false,
      });

  const applied = amount.tax.applied[0];
  const tax: TaxAppliedInfo | null =
    applied && amount.tax.total > 0
      ? {
          id: applied.id,
          percentage: applied.percentage,
          amount: amount.tax.total,
        }
      : null;

  const isFree = basePricePerMonth === 0 && unitPrice === 0;

  const perUnitPrice = committedQty > 0 ? roundPrice(subtotalPerMonth / committedQty) : 0;

  return {
    planCode,
    model,
    interval,
    currency,
    billingBasis,
    committedQty,
    committedQtyForPeriod: committedQty * monthsInInterval,
    vehicleQty: isUsage ? 0 : committedQty,
    monthsInInterval,
    subtotal: amount.subtotal,
    tax,
    total: amount.total,
    isFree,
    perUnitPrice,
    perTruckPerMonth: perUnitPrice,
  };
}

/**
 * How many months one billing period of a given interval covers.
 *
 * Exposed on the preview as `monthsInInterval` so the UI can render
 * the breakdown ("Billed for N months") without re-deriving the
 * constant. Also used by `BillingPricingService.computeForSubscription`
 * to inflate per-month rates into the full-period unit price stored
 * on each `BillingItem`.
 */
export function getMonthsInInterval(interval: SubscriptionInterval): number {
  switch (interval) {
    case 'month':
      return 1;
    case 'semester':
      return 6;
    case 'year':
      return 12;
  }
}
